import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

type TableRegionStyle = CSSProperties & { "--table-min-width": string };

/**
 * Contains dense comparison data without letting its minimum width escape into
 * the document. The region is keyboard focusable and its edge fade is a visual
 * cue that additional columns remain horizontally available.
 *
 * Use a table when comparing columns is the task. Prefer compact cards with
 * disclosure when each row is primarily opened or acted on independently.
 */
export function TableScrollRegion({
  children,
  label,
  minWidth = "640px",
  stickyFirstColumn = false,
  stickyHeader = false,
}: {
  children: ReactNode;
  label: string;
  minWidth?: string;
  stickyFirstColumn?: boolean;
  stickyHeader?: boolean;
}) {
  const region = useRef<HTMLDivElement>(null);
  const [atEnd, setAtEnd] = useState(true);
  const [overflowing, setOverflowing] = useState(false);

  const measure = () => {
    const element = region.current;
    if (!element) return;
    const overflows = element.scrollWidth > element.clientWidth + 1;
    setOverflowing(overflows);
    setAtEnd(
      !overflows ||
        element.scrollLeft + element.clientWidth >= element.scrollWidth - 1,
    );
  };

  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === "undefined" || !region.current) return;
    const observer = new ResizeObserver(measure);
    observer.observe(region.current);
    return () => observer.disconnect();
  }, [children]);

  return (
    <div className={`table-scroll-frame${atEnd ? " is-at-end" : ""}`}>
      <div
        ref={region}
        className={`table-scroll-region${stickyFirstColumn ? " has-sticky-first-column" : ""}${stickyHeader ? " has-sticky-header" : ""}`}
        role="region"
        aria-label={label}
        tabIndex={0}
        data-overflow={overflowing || undefined}
        onScroll={measure}
        style={{ "--table-min-width": minWidth } as TableRegionStyle}
      >
        {children}
      </div>
      {overflowing && (
        <span className="visually-hidden">
          Scroll horizontally for more columns.
        </span>
      )}
    </div>
  );
}
